import { action, computed, observable } from 'mobx'
import type {
  MultiSelectAnswer,
  SelectQuestion,
  SelectQuestionOption,
  SingleSelectAnswer,
} from '@/features/jobs/questions/questions'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'
import { deepEqual, isNotNil } from '@/utils/object-util'
import { truncateTextItems } from '@/utils/string-util'
import { shallowItemsAreEqual } from '@/utils/array-util'

/**
 * Question answer editor for `Select` questions.
 *
 * `deepEqual` guards keep reconciliations without structural changes from
 * re-rendering.
 */
export class SelectQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<SelectQuestion> {
  /** @inheritDoc */
  readonly type = 'Select'

  @observable.ref accessor value: string[] = []
  @observable accessor multi = false
  @observable.ref accessor options: SelectQuestion['options'] = []

  /**
   * The selected options. Selections no longer among the options show their ID.
   */
  @computed get selected(): SelectQuestionOption[] {
    return this.value
      .filter(Boolean)
      .map(
        (id) =>
          this.options.find((option) => option.id === id) ?? { id, text: id },
      )
  }

  /** @inheritDoc */
  @computed get isAnswered(): boolean {
    return this.selected.length > 0
  }

  /**
   * A multi-select is sent even when nothing is selected, so deselecting
   * everything clears the answer.
   *
   * @inheritDoc
   */
  @computed get answer(): SingleSelectAnswer | MultiSelectAnswer | null {
    const value = this.value.filter(isNotNil)
    if (this.multi) {
      return { type: 'MultiSelectAnswer', value }
    }

    return value.length > 0
      ? { type: 'SingleSelectAnswer', value: value[0] }
      : null
  }

  /**
   * Whether the option is selected.
   *
   * @param option
   */
  isSelected(option: SelectQuestionOption) {
    return this.selected.some((s) => s.id === option.id)
  }

  /** @inheritDoc */
  set(question: SelectQuestion) {
    if (!deepEqual(this.options.slice(), question.options)) {
      this.options = question.options.slice()
    }
    this.multi = question.multi

    const answer = question.answer
    if (!answer) {
      this.value = []
      return
    }

    switch (answer.type) {
      case 'MultiSelectAnswer':
        if (!deepEqual(this.value, answer.value)) {
          this.value = answer.value
        }
        return
      case 'SingleSelectAnswer':
        if (answer.value !== '' && this.value[0] !== answer.value) {
          this.value = [answer.value]
        }
        return
    }
  }

  /**
   * Responds to the question.
   *
   * @param selections
   */
  @action.bound
  respond(selections: SelectQuestionOption[]) {
    const ids = selections.map((o) => o.id)
    if (shallowItemsAreEqual(ids, this.value)) {
      return
    }
    this.value = ids
    this.notifyAnswerChanged()
  }

  /**
   * Toggles the option: multi-selects add or remove it, single-selects select
   * it.
   *
   * @param option
   */
  @action.bound
  toggleOption(option: SelectQuestionOption) {
    if (this.multi) {
      this.respond(
        this.isSelected(option)
          ? this.selected.filter((s) => s.id !== option.id)
          : [...this.selected, option],
      )
      return
    }

    if (this.isSelected(option)) {
      return
    }

    this.respond([option])
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    return this.isAnswered
      ? QuestionValidationResult.ok()
      : QuestionValidationResult.failed(['Please select something.'])
  }

  /** @inheritDoc */
  protected formatTextAnswer(maxLength: number): string {
    if (!this.multi) {
      return this.selected[0]?.text ?? ''
    }

    return truncateTextItems(
      this.selected.map((s) => s.text),
      maxLength,
    )
  }
}
